import { Dictionary } from 'leanplum-lib-common';
import fromPairs from 'lodash/fromPairs';
import isEqual from 'lodash/isEqual';
import pluralize from 'pluralize';
import Vue, { VNode } from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { cx } from '../../utils/cx';
import { Checkbox } from '../Checkbox/Checkbox';
import { Pagination } from '../Pagination/Pagination';
import { Separator } from '../Separator/Separator';
import { ItemRow } from './ItemRow';
import { CellContent, ItemComparator, ItemPredicate, Renderable } from './types';
import { computeGridTemplateColumnsStyle } from './utils';

require('./Table.scss');

/**
 * Events:
 *   onRowClick(item: Item);
 *   onRowSelect(item: Item, value: boolean);
 *   onSelectAll(items: Array<Item>, value: boolean);
 *   onSelectionChange(items: Array<Item>)
 *   onChange(pageIndex: number, columns: Array<Table.ColumnDescriptor<Item>>, filter: Filter);
 *   onCurrentItemsChange(pageItems: Array<Item>)
 */
@Component({ name: 'Table' })
class Table<Item extends object, Filter = unknown> extends Vue implements Table.Props<Item, Filter> {
  static readonly SLOT_EMPTY_STATE = 'emptyState';
  static readonly SLOT_NO_RESULTS_FOUND = 'noSearchResults';
  static readonly SLOT_ACTIONS = 'actions';
  static readonly SLOT_ITEM_MENU = 'itemMenu';
  static readonly SLOT_BULK_SELECT_MENU = 'bulkSelectMenu';

  static readonly EVENT_ROW_CLICK = 'rowClick';
  static readonly EVENT_ROW_SELECT = 'rowSelect';
  static readonly EVENT_SELECT_ALL = 'selectAll';
  static readonly EVENT_SELECTION_CHANGE = 'selectionChange';
  static readonly EVENT_CHANGE = 'change';
  static readonly EVENT_CURRENT_ITEMS_CHANGE = 'currentItemsChange';

  static readonly CLASS_LOADING_PLACEHOLDER = 'lp-table-loading-placeholder';
  static readonly CLASS_EMBEDDED = 'embedded';
  static readonly CLASS_HIGHLIGHT_CELL = 'highlighted';

  private static readonly ACTIONS_SLOT_HEIGHT = 72;
  private static readonly FOOTER_HEIGHT = 69;

  @Prop({ type: Array, required: false, default: (): Array<Item> => [] })
  readonly items: Array<Item>;

  @Prop({ type: Array, required: true })
  readonly columnDescriptors: Array<Table.ColumnDescriptor<Item, Filter>>;

  @Prop({ type: Boolean, required: false, default: false })
  readonly loading: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  readonly embedded: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  readonly selectable: boolean;

  @Prop({ type: Number, required: false, default: 10 })
  readonly itemsPerPage: number;

  @Prop({ type: Function, required: true })
  readonly itemKeyGetter: Table.ItemKeyGetter<Item>;

  @Prop({ type: Array, required: false, default: (): Array<Item> => [] })
  readonly selected: Array<Item>;

  @Prop({ type: String, required: false, default: (): Table.RowStyle => Table.RowStyle.BORDERED })
  readonly rowStyle: Table.RowStyle;

  @Prop({ type: [String, Number, Object, Array], required: false, default: null })
  readonly filter: Filter | null;

  @Prop({ type: Object, required: false, default: null })
  readonly scrollStyle: Table.ScrollStyle | null;

  @Prop({ type: Function, required: false, default: (): boolean => true })
  readonly clickablePredicate: ItemPredicate<Item>;

  @Prop({ type: Function, required: false, default: (): boolean => true })
  readonly hoverablePredicate: ItemPredicate<Item>;

  @Prop({ type: String, required: false, default: '' })
  readonly itemName: string;

  readonly $scopedSlots: {
    /**
     * Appears on row hover and replaces last column cell
     */
    [Table.SLOT_ITEM_MENU]?: (item: Item) => Array<VNode>;
    /**
     * Appears in footer when items are selected
     */
    [Table.SLOT_BULK_SELECT_MENU]?: (items: Array<Item>) => Array<VNode>;
  };

  readonly $slots: {
    [Table.SLOT_ACTIONS]?: Array<VNode>;
    [Table.SLOT_NO_RESULTS_FOUND]?: Array<VNode>;
    [Table.SLOT_EMPTY_STATE]?: Array<VNode>;
  };

  private pageIndex: number = 1;
  private selectedItemsKeys: Array<string> = this.selected.map(this.itemKeyGetter);

  private get visibleColumnDescriptors(): Array<Table.ColumnDescriptor<Item>> {
    return this.columnDescriptors.filter((column) => column.visible ?? true);
  }

  private get comparatorDescriptors(): Array<ComparatorDescriptor<Item>> {
    return this.visibleColumnDescriptors
      .filter((column) => column.sortable)
      .map((column) => ({
        direction: column.sortDirection ?? Table.SortDirection.ASCENDING,
        comparator: column.comparator ?? Table.createDefaultColumnComparator(column)
      }));
  }

  private get filterableColumnDescriptors(): Array<Table.ColumnDescriptor<Item>> {
    return this.visibleColumnDescriptors.filter((column) => column.filterable);
  }

  private get filteredItems(): Array<Item> {
    if (this.filterableColumnDescriptors.length === 0) {
      return this.items;
    }

    return this.items.filter((item) =>
      this.filterableColumnDescriptors.some(
        (col) => col.filter?.(item, this.filter) ?? this.defaultItemFilter(item, col)
      )
    );
  }

  private get sortedAndFilteredItems(): Array<Item> {
    if (this.comparatorDescriptors.length === 0) {
      return this.filteredItems;
    }

    return this.filteredItems.slice().sort((a, b) => {
      const differentiatingDescriptor = this.comparatorDescriptors.find(({ comparator }) => comparator(a, b) !== 0);

      if (!differentiatingDescriptor) {
        return 0;
      }

      const directionCoefficient = differentiatingDescriptor.direction === Table.SortDirection.DESCENDING ? -1 : 1;

      return differentiatingDescriptor.comparator(a, b) * directionCoefficient;
    });
  }

  private get pageItems(): Array<Item> {
    const start = (this.pageIndex - 1) * this.itemsPerPage;
    const end = this.pageIndex * this.itemsPerPage;

    return this.sortedAndFilteredItems.slice(start, end);
  }

  private get pageCount(): number {
    return Math.ceil(this.sortedAndFilteredItems.length / this.itemsPerPage);
  }

  private get filteredItemsCount(): number {
    return this.filteredItems.length;
  }

  private get hasNoData(): boolean {
    return !this.loading && this.items.length === 0;
  }

  private get hasNoResults(): boolean {
    return !this.loading && this.items.length > 0 && this.filteredItems.length === 0;
  }

  private get isEveryPageItemSelected(): boolean {
    return (
      !this.loading &&
      this.filteredItems.length !== 0 &&
      this.pageItems.every((item) => this.selectedItemsKeysSet.has(this.itemKeyGetter(item)))
    );
  }

  private get isSomePageItemSelected(): boolean {
    return (
      !this.loading &&
      this.filteredItems.length !== 0 &&
      this.pageItems.some((item) => this.selectedItemsKeysSet.has(this.itemKeyGetter(item)))
    );
  }

  private get tableWrapperStyle(): Partial<CSSStyleDeclaration> | null {
    if (!this.scrollStyle) {
      return null;
    }

    const actionSlotAccommodation = this.$slots[Table.SLOT_ACTIONS] ? Table.ACTIONS_SLOT_HEIGHT : 0;

    return fromPairs(
      Object.entries(this.scrollStyle).map(([property, height]) => [
        property,
        `calc(${height} - ${actionSlotAccommodation}px - ${Table.FOOTER_HEIGHT}px)`
      ])
    );
  }

  private get headerStyles(): Partial<CSSStyleDeclaration> {
    return computeGridTemplateColumnsStyle(this.visibleColumnDescriptors, this.selectable);
  }

  private get keyedItems(): Dictionary<Item> {
    return this.items.reduce<Dictionary<Item>>((map, item) => {
      map[this.itemKeyGetter(item)] = item;

      return map;
    }, {});
  }

  private get selectedItems(): Array<Item> {
    return this.selectedItemsKeys.map((key) => this.keyedItems[key]);
  }

  private get selectedItemsKeysSet(): Set<string> {
    return new Set(this.selectedItemsKeys);
  }

  private get pageItemsKeys(): Array<string> {
    return this.pageItems.map(this.itemKeyGetter);
  }

  private get hasSelectedAllItems(): boolean {
    return this.selectedItemsKeys.length === this.filteredItemsCount;
  }

  private get pluralizedItemName(): string {
    if (this.filteredItemsCount === 1) {
      return this.itemName;
    }

    return pluralize(this.itemName);
  }

  private get formattedFilteredItemCount(): string {
    return this.filteredItemsCount.toLocaleString();
  }

  private get footerSummary(): string {
    const firstPageItemHumanIndex = Math.min(
      (this.pageIndex - 1) * this.itemsPerPage + 1,
      this.filteredItemsCount
    ).toLocaleString();

    const lastPageItemHumanIndex = Math.min(
      this.pageIndex * this.itemsPerPage,
      this.filteredItemsCount
    ).toLocaleString();

    return `${firstPageItemHumanIndex} - ${lastPageItemHumanIndex} of ${this.formattedFilteredItemCount} ${this.pluralizedItemName}`;
  }

  private get selectedSummary(): string {
    const selectedCountFormatted = this.selectedItemsKeys.length.toLocaleString();
    const summary = `${selectedCountFormatted} ${this.pluralizedItemName} selected`;

    if (this.hasSelectedAllItems) {
      return `All ${summary}`;
    }

    return summary;
  }

  private get selectAllLabel(): string {
    return `Select All ${this.formattedFilteredItemCount}`;
  }

  @Watch('filter')
  @Watch('columnDescriptors')
  @Watch('pageIndex')
  onStateUpdate(): void {
    this.$emit(Table.EVENT_CHANGE, this.pageIndex, this.columnDescriptors, this.filter);
  }

  @Watch('pageItemsKeys')
  onPageItemsChange(newValue: Array<string>, oldValue: Array<string>): void {
    if (isEqual(newValue, oldValue)) {
      return;
    }

    this.$emit(Table.EVENT_CURRENT_ITEMS_CHANGE, this.pageItems);
  }

  @Watch('selected')
  onSelectedItemsPropChange(): void {
    this.selectedItemsKeys = this.selected.map(this.itemKeyGetter);
  }

  @Watch('filteredItemsCount')
  onFilteredItemsCountChange(): void {
    this.pageIndex = 1;
  }

  render(): VNode {
    return (
      <div class={cx('lp-table', { [Table.CLASS_EMBEDDED]: this.embedded })} style={this.scrollStyle}>
        {this.$slots[Table.SLOT_ACTIONS] && <div class="lp-table-actions">{this.$slots[Table.SLOT_ACTIONS]}</div>}
        <div class="lp-table-body" style={this.tableWrapperStyle}>
          {this.renderHeader()}
          {this.loading ? this.renderLoadingRows() : this.pageItems.map(this.renderRow)}
          {this.hasNoResults && this.$slots[Table.SLOT_NO_RESULTS_FOUND]}
          {this.hasNoData && this.$slots[Table.SLOT_EMPTY_STATE]}
        </div>
        {this.renderFooter()}
      </div>
    );
  }

  private renderHeader(): VNode {
    return (
      <div class="lp-table-header" style={this.headerStyles}>
        {this.selectable && (
          <div class="lp-table-select-all">
            <Checkbox
              checked={this.isEveryPageItemSelected}
              indeterminate={!this.isEveryPageItemSelected && this.isSomePageItemSelected}
              disabled={this.loading}
              onChange={this.onSelectAll}
            />
          </div>
        )}
        {this.visibleColumnDescriptors.map(this.renderColumnCell)}
      </div>
    );
  }

  private renderLoadingRows(): Array<VNode> {
    return Array(15)
      .fill(null)
      .map(this.renderLoadingRow);
  }

  private renderRow(item: Item, index: number): VNode {
    return (
      <ItemRow
        item={item}
        index={index}
        itemKeyGetter={this.itemKeyGetter}
        rowStyle={this.rowStyle}
        clickablePredicate={this.clickablePredicate}
        hoverablePredicate={this.hoverablePredicate}
        selectable={this.selectable}
        selected={this.selectedItemsKeysSet.has(this.itemKeyGetter(item))}
        columns={this.visibleColumnDescriptors}
        scopedSlots={this.$scopedSlots}
        onClick={this.onRowClick}
        onSelect={this.onSelect}
      />
    );
  }

  private renderLoadingRow(x: unknown, index: number): VNode {
    return (
      <ItemRow
        rowStyle={this.rowStyle}
        clickablePredicate={this.clickablePredicate}
        hoverablePredicate={this.hoverablePredicate}
        columns={this.visibleColumnDescriptors}
        loading={true}
        selectable={this.selectable}
        index={index}
      />
    );
  }

  private renderFooter(): VNode | null {
    if (this.hasNoData || this.hasNoResults) {
      return null;
    }

    if (this.itemsPerPage < 0) {
      return <div class="lp-table-footer-accommodation" />;
    }

    const bulkMenuSlot = this.$scopedSlots[Table.SLOT_BULK_SELECT_MENU];

    return (
      <div class="lp-table-footer">
        {this.renderFooterSummary()}
        {this.selectedItemsKeys.length > 0 && bulkMenuSlot && [<Separator />, bulkMenuSlot(this.selectedItems)]}
        {this.pageCount > 1 && (
          <Pagination current={this.pageIndex} count={this.pageCount} onChange={this.onPageChange} />
        )}
      </div>
    );
  }

  private renderFooterSummary(): Array<VNode | boolean> | VNode | null {
    if (this.loading) {
      return null;
    }

    if (this.selectedItemsKeys.length === 0) {
      return <div class="lp-table-footer-pagination-info">{this.footerSummary}</div>;
    }

    return [
      <div class="lp-table-footer-selection-info">{this.selectedSummary}</div>,
      this.isEveryPageItemSelected && !this.hasSelectedAllItems && (
        <div class="lp-table-footer-bulk-select-action" onClick={this.selectAll}>
          {this.selectAllLabel}
        </div>
      ),
      <div class="lp-table-footer-bulk-select-action" onClick={this.unselectAll}>
        Clear Selection
      </div>
    ];
  }

  private renderColumnCell(column: Table.ColumnDescriptor<Item>): VNode {
    const className = cx(
      'lp-table-cell',
      { [Table.CLASS_HIGHLIGHT_CELL]: column.highlight },
      column.align ?? Table.CellAlignment.START
    );

    return <div class={className}>{this.renderColumnContent(column)}</div>;
  }

  private renderColumnContent(column: Table.ColumnDescriptor<Item>): CellContent<Item> {
    return 'headerRenderer' in column ? column.headerRenderer(column) : column.label;
  }

  private onPageChange(page: number): void {
    this.pageIndex = page;
  }

  private onSelectAll(value: boolean): void {
    const keys = new Set(this.pageItemsKeys);

    if (value) {
      this.selectedItemsKeys = [...new Set([...this.selectedItemsKeys, ...keys])];
    } else {
      this.selectedItemsKeys = this.selectedItemsKeys.filter((x) => !keys.has(x));
    }

    this.$emit(Table.EVENT_SELECT_ALL, this.pageItems, value);
    this.onSelectionChange();
  }

  private onSelect(itemKey: string, value: boolean): void {
    if (value) {
      this.selectedItemsKeys.push(itemKey);
    } else {
      this.selectedItemsKeys = this.selectedItemsKeys.filter((x) => x !== itemKey);
    }

    const item = this.keyedItems[itemKey];
    this.$emit(Table.EVENT_ROW_SELECT, item, value);
    this.onSelectionChange();
  }

  private onRowClick(item: Item): void {
    this.$emit(Table.EVENT_ROW_CLICK, item);
  }

  private onSelectionChange(): void {
    this.$emit(Table.EVENT_SELECTION_CHANGE, this.selectedItems);
  }

  private selectAll(): void {
    this.selectedItemsKeys = Object.keys(this.keyedItems);
    this.onSelectionChange();
  }

  private unselectAll(): void {
    this.selectedItemsKeys = [];
    this.onSelectionChange();
  }

  private defaultItemFilter(item: Item, column: Table.ColumnDescriptor<Item>): boolean {
    if (!this.filter) {
      return true;
    }

    const supportedTypes = new Set(['string', 'number', 'boolean']);
    const supportedTypesString = [...supportedTypes].join(', ');

    if (!supportedTypes.has(typeof this.filter)) {
      console.warn(
        `Cannot apply default filter for filter type ${typeof this.filter}, expected ${supportedTypesString}`,
        column
      );

      return false;
    }

    if (!('key' in column)) {
      console.warn(`Cannot apply default filter when column does not provide mapping key`, column);

      return false;
    }

    const cellValue = item[column.key];

    if (!supportedTypes.has(typeof cellValue)) {
      console.warn(
        `Cannot apply default filter for cell value type ${typeof cellValue}, expected ${supportedTypesString}`,
        column,
        item
      );

      return false;
    }

    return String(cellValue)
      .toLowerCase()
      .includes(String(this.filter).toLowerCase());
  }

  private static defaultValueComparator<Item>(a: Item[keyof Item], b: Item[keyof Item]): number {
    if (typeof a === 'string' && typeof b === 'string') {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    }

    return Number(a < b ? -1 : a > b);
  }

  private static createDefaultColumnComparator<Item>(column: Table.ColumnDescriptor<Item>): ItemComparator<Item> {
    return (a: Item, b: Item): number => {
      if ('key' in column) {
        return Table.defaultValueComparator(a[column.key], b[column.key]);
      }

      console.warn(`Cannot sort a column without providing a mapping key or custom comparator`, column);

      return 0;
    };
  }
}

interface ComparatorDescriptor<Item> {
  comparator: ItemComparator<Item>;
  direction: Table.SortDirection;
}

namespace Table {
  export interface Props<Item, Filter = unknown> {
    /**
     * renders itemsPerPage rows that render
     * loading placeholders per column
     */
    loading?: boolean;

    /**
     * wether to remove shadow box and border-radius of table container
     */
    embedded?: boolean;

    /**
     * property that is an unique key for each item
     * You can provide deep path ex.: "segment.name"
     */
    itemKeyGetter: ItemKeyGetter<Item>;

    columnDescriptors: Array<ColumnDescriptor<Item, Filter>>;

    items: Array<Item>;

    /**
     * renders select row checkboxes
     * TODO: consider having a descriptor with:
     *  - default selected values
     *  - disabling select all
     */
    selectable?: boolean;

    /**
     * Amount of items per table page
     * If -1 is provided, all items are going to be rendered on 1 page, without rendering the footer
     */
    itemsPerPage?: number;

    /**
     * Array of preselected items.
     */
    selected?: Array<Item>;

    rowStyle?: Table.RowStyle;

    /**
     * Additional argument that is passed to columnDescriptor.filter
     * Calls onChange when it gets updates
     * By default if it is an instance of string it will be used to filter (case-insensitive)
     * all filterable columns by checking wether it is contained in item[columnDescriptor.key]
     */
    filter?: Filter | null;

    /**
     * Defines max height for the table and allows it to be scrollable.
     */
    scrollStyle?: ScrollStyle | ScrollStyle | null;

    /**
     * Called for each item, applies clickable styles if the predicate returns true.
     * @param item
     * @param index
     */
    clickablePredicate?: ItemPredicate<Item>;

    /**
     * Called for each item, applies hoverable styles if the predicate returns true.
     * @param item
     * @param index
     */
    hoverablePredicate?: ItemPredicate<Item>;

    /**
     * Name of entity in singular form ex.: "campaign", "audience"
     */
    itemName?: string;
  }

  export type ColumnDescriptor<Item, Filter = unknown> = ColumnDescriptorOptionalFields<Item, Filter> &
    (ColumnDescriptorWithKey<Item> | ColumnDescriptorWithItemRenderer<Item>) &
    (ColumnDescriptorWithHeaderLabel | ColumnDescriptorWithHeaderRenderer<Item>);

  export interface ColumnDescriptorWithKey<Item> {
    /**
     * Renders string value of item[key]
     * Value is used for default sorting comparator
     * and default filter predicate
     * If omitted you would need to provide custom comparator
     */
    key: keyof Item;
  }

  export interface ColumnDescriptorWithItemRenderer<Item> {
    /**
     * custom template for each item cell
     */
    itemRenderer: (item: Item, column: ColumnDescriptor<Item>, isRowHovered: boolean) => Renderable;
  }

  export interface ColumnDescriptorWithHeaderLabel {
    label: string;
  }

  export interface ColumnDescriptorWithHeaderRenderer<Item> {
    /**
     * custom template for each head cell
     */
    headerRenderer: (column: ColumnDescriptor<Item>) => Renderable;
  }

  export interface ColumnDescriptorOptionalFields<Item, Filter = unknown> {
    align?: Table.CellAlignment;

    width?: string;

    visible?: boolean;

    sortable?: boolean;

    /**
     * Renders loading placeholders only for that column.
     */
    loading?: boolean;

    /**
     * Default is SortDirection.ASCENDING
     */
    sortDirection?: Table.SortDirection;

    /**
     * Default is false;
     */
    hideOnHover?: boolean;

    // TODO: consider adding sortPriority: number;

    /**
     * Provide in ascending order.
     * Default one compares item[key].valueOf()
     */
    comparator?: ItemComparator<Item>;

    filterable?: boolean;

    /**
     * Applies highlighted typography to header.
     */
    highlight?: boolean;

    /**
     * Predicate which filters items rows
     */
    filter?: (item: Item, filter: Filter) => boolean;
  }

  export enum CellAlignment {
    START = 'start',
    CENTER = 'center',
    END = 'end',
    STRETCH = 'stretch'
  }

  export enum SortDirection {
    ASCENDING = 'ascending',
    DESCENDING = 'descending'
  }

  export enum RowStyle {
    STRIPED = 'striped',
    BORDERED = 'bordered'
  }

  export interface ScrollStyle {
    maxHeight?: string;
    minHeight?: string;
    height?: string;
  }

  export type ItemKeyGetter<Item> = (item: Item) => string;
}

export { Table };
